import { useState, useCallback, useRef, useEffect } from "react";

/**
 * useCropOverlay — manages a draggable + resizable crop selection box.
 *
 * Returns:
 *   cropState    : { x, y, width, height, active } — current box position/size in px
 *   overlayProps : event handlers to spread on the overlay container div
 *   boxRef       : ref to attach to the selection box div
 *   startCrop    : () => void — activates the overlay
 *   cancelCrop   : () => void — hides the overlay
 *   getCropRect  : () => { x, y, width, height } — returns final pixel coordinates
 *
 * The coordinates are relative to the overlay container (which should match
 * the media element's bounding area).
 */

const MIN_SIZE = 40;

export default function useCropOverlay() {
  const [active, setActive] = useState(false);
  const [box, setBox] = useState({ x: 50, y: 50, width: 200, height: 200 });
  const dragRef = useRef(null); // tracks ongoing drag/resize operation
  const containerRef = useRef(null);
  const boxRef = useRef(null);

  const startCrop = useCallback(() => {
    setActive(true);
    setBox({ x: 50, y: 50, width: 200, height: 200 });
  }, []);

  const cancelCrop = useCallback(() => {
    setActive(false);
  }, []);

  const getCropRect = useCallback(() => {
    return { x: box.x, y: box.y, width: box.width, height: box.height };
  }, [box]);

  // ── Pointer handlers ──

  const onPointerDownBox = useCallback(
    (e) => {
      if (!active) return;
      e.stopPropagation();
      e.preventDefault();
      const target = e.target;
      const handle = target.dataset?.handle;

      const startX = e.clientX;
      const startY = e.clientY;
      const startBox = { ...box };

      dragRef.current = { type: handle || "move", startX, startY, startBox };
      document.body.style.userSelect = "none";
      document.body.style.cursor = handle ? `${handle}-resize` : "move";

      const onMove = (ev) => {
        if (!dragRef.current) return;
        const dx = ev.clientX - dragRef.current.startX;
        const dy = ev.clientY - dragRef.current.startY;
        const s = dragRef.current.startBox;
        const containerEl = containerRef.current;
        const bounds = containerEl
          ? { w: containerEl.offsetWidth, h: containerEl.offsetHeight }
          : { w: 9999, h: 9999 };

        let newX = s.x;
        let newY = s.y;
        let newW = s.width;
        let newH = s.height;

        const op = dragRef.current.type;

        if (op === "move") {
          newX = Math.max(0, Math.min(s.x + dx, bounds.w - s.width));
          newY = Math.max(0, Math.min(s.y + dy, bounds.h - s.height));
        } else {
          // Resize from edges/corners
          if (op.includes("e")) {
            newW = Math.max(MIN_SIZE, Math.min(s.width + dx, bounds.w - s.x));
          }
          if (op.includes("w")) {
            const maxDx = s.width - MIN_SIZE;
            const clampedDx = Math.max(-s.x, Math.min(dx, maxDx));
            newX = s.x + clampedDx;
            newW = s.width - clampedDx;
          }
          if (op.includes("s")) {
            newH = Math.max(MIN_SIZE, Math.min(s.height + dy, bounds.h - s.y));
          }
          if (op.includes("n")) {
            const maxDy = s.height - MIN_SIZE;
            const clampedDy = Math.max(-s.y, Math.min(dy, maxDy));
            newY = s.y + clampedDy;
            newH = s.height - clampedDy;
          }
        }

        setBox({ x: newX, y: newY, width: newW, height: newH });
      };

      const onUp = () => {
        dragRef.current = null;
        document.body.style.userSelect = "";
        document.body.style.cursor = "";
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
      };

      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
    },
    [active, box]
  );

  // ── Build the overlay props ──

  const cropState = { ...box, active };

  return {
    cropState,
    containerRef,
    boxRef,
    startCrop,
    cancelCrop,
    getCropRect,
    onPointerDownBox,
  };
}